import type { ReactNode } from 'react';
import { ChakraProvider, Portal, Spinner, Stack, Toast, Toaster, createSystem, createToaster, defaultConfig, defineConfig } from '@chakra-ui/react';
import { ACCENT_COLOR, ACCENT_TINT, GROUP_BORDER_COLOR, GROUP_DIVIDER_COLOR, GROUP_TITLE_COLOR, LINE_COLOR, MENU_BACKGROUND, MUTED_COLOR, RAISED_BACKGROUND, UNSAVED_COLOR } from './theme.ts';

// Chakra UI for the app's chrome: settings, forms, dialogs, messages (architecture decision §5:
// "Chakra = chrome, Canvas/SVG = content"). The tiles keep the legacy look and draw themselves.
// The `casa.*` tokens are the chrome's own (look LK-9): the side menu, the phone bar, the settings,
// the dialogs and the admin page use them. Always dark, like the legacy app (the `dark` class on <html>, index.html).
// Chakra's CSS reset applies only inside elements with the class `chrome` (the pages, the phone
// bar, dialogs, the drawer, messages): the tiles and the screens are laid out by our own styles, and
// the reset would change them (box sizing, borders, line heights).

const config = defineConfig({
    preflight: { scope: '.chrome', level: 'parent' },
    theme: {
        tokens: {
            fonts: {
                heading: { value: 'system-ui, sans-serif' },
                body: { value: 'system-ui, sans-serif' },
            },
            colors: {
                casa: {
                    accent: { value: ACCENT_COLOR },
                    accentTint: { value: ACCENT_TINT },
                    panel: { value: MENU_BACKGROUND },
                    raised: { value: RAISED_BACKGROUND },
                    line: { value: LINE_COLOR },
                    muted: { value: MUTED_COLOR },
                    unsaved: { value: UNSAVED_COLOR },
                    groupTitle: { value: GROUP_TITLE_COLOR },
                    groupBorder: { value: GROUP_BORDER_COLOR },
                    groupDivider: { value: GROUP_DIVIDER_COLOR },
                },
            },
            radii: {
                casa: { value: '12px' },
            },
        },
    },
});

export const system = createSystem(defaultConfig, config);

/** Messages at the bottom of the screen: a save's result, a failure. */
export const toaster = createToaster({ placement: 'bottom', pauseOnPageIdle: true, overlap: false, max: 3 });

function AppToaster() : ReactNode {
    return (
        <Portal>
            <Toaster toaster={toaster} className="chrome" insetInline={{ mdDown: '4' }}>
                {(toast) => (
                    <Toast.Root width={{ md: 'md' }}>
                        {toast.type === 'loading' ? <Spinner size="sm" color="blue.solid" /> : <Toast.Indicator />}
                        <Stack gap="1" flex="1" maxWidth="100%">
                            {toast.title ? <Toast.Title>{toast.title}</Toast.Title> : null}
                            {toast.description ? <Toast.Description>{toast.description}</Toast.Description> : null}
                        </Stack>
                        <Toast.CloseTrigger />
                    </Toast.Root>
                )}
            </Toaster>
        </Portal>
    );
}

export function ChromeProvider({ children }: { children: ReactNode }) : ReactNode {
    return (
        <ChakraProvider value={system}>
            {children}
            <AppToaster />
        </ChakraProvider>
    );
}
